import clsx from 'clsx';
import React from 'react';
import SidebarCrossIcon from '../../assets/icons/sidebar-cross-icon.svg?react';
import EditIcon from '../../assets/icons/edit-icon.svg?react';
import { OshaReportingControllerGetOshaSubmissionDetailsStatusEnum } from '@EHSNavigator/backend';
import Button from '../Button';
import { useNavigate } from 'react-router';
import RefreshIcon from '../../assets/icons/refresh-icon.svg?react';

interface OshaSummaryModalProps {
  otherClasses?: string;
  year?: number;
  data: any;
  handleClose?: () => void;
  isLogsNotAvailable?:boolean
}

const OshaSummaryModal: React.FC<OshaSummaryModalProps> = ({
  otherClasses,
  data,
  handleClose,
  year,
  isLogsNotAvailable=false,
}) => {
  const oshaSummaryModalClasses = clsx(otherClasses, !handleClose && 'mt-10');

  const errors: any = data?.failedReason?.error || [];
  const navigate = useNavigate();

  return (
    <div className={oshaSummaryModalClasses} data-testid="osha-summary-modal">
      <div className="mb-6 flex w-full items-center justify-between">
        <h4 className="text-brand-byzantine flex items-center gap-2">
          {handleClose && (
            <EditIcon className="h-10 min-h-10 w-10 min-w-10 [&>path]:fill-current" />
          )}
          {handleClose ? `View Summary (OSHA 300A)` : `View Summary`}
        </h4>
        {handleClose && (
          <button className="cursor-pointer" onClick={handleClose}>
            <SidebarCrossIcon className="[&>g>path]:fill-brand-posh-pink h-6 w-6" />
          </button>
        )}

        {year && (
          <Button
            onClick={() => navigate(`/osha-reporting/resubmit?year=${year}`)}
            otherClasses=" !py-0 h-full whitespace-nowrap !gap-0"
            size="smaller"
            disable={!isLogsNotAvailable}
          >
            <RefreshIcon className="!mr-2.5 h-5 w-5 [&>g>path]:stroke-current" />
            Resubmit <span className="ml-1 hidden lg:block">To Osha</span>
          </Button>
        )}
      </div>

      <div className="mt-9 flex flex-col gap-6">
        {errors?.length > 0 &&
          data?.status ===
            OshaReportingControllerGetOshaSubmissionDetailsStatusEnum.Failed && (
            <div className="border-neutral-tones-text/35 rounded-10 bg-card-backgrounds/5 flex flex-col overflow-hidden border sm:col-span-7 sm:gap-8">
              <div className="bg-brand-byzantine w-full p-15">
                <h4 className="text-neutral-tones-text-revert">Error</h4>
              </div>
              <div className="flex flex-wrap justify-between gap-y-5 px-15 pt-15 pb-15 lg:gap-y-9 lg:pt-0">
                <p className="text-brand-posh-pink font-inter-variable lg:-tracking-form-input col-span-2 text-sm leading-4 font-normal lg:text-base lg:leading-5">
                  {errors?.map((node: string) => node) || '---'}
                </p>
              </div>
            </div>
          )}
        <div className="border-neutral-tones-text/35 rounded-10 bg-card-backgrounds/5 flex flex-col overflow-hidden border sm:col-span-7 sm:gap-8">
          <div className="bg-brand-byzantine w-full p-15">
            <h4 className="text-neutral-tones-text-revert">Number of Cases</h4>
          </div>
          <div className="flex flex-wrap justify-between gap-y-5 px-15 pt-15 pb-15 lg:gap-y-9 lg:pt-0">
            <div className="flex w-full max-w-275 flex-col">
              <h4 className="text-brand-byzantine">
                {data?.response?.total_deaths}
              </h4>
              <p className="text-neutral-tones-text font-inter-variable text-sm leading-5 font-bold uppercase lg:text-base">
                TOTAL NUMBER OF DEATHS
              </p>
            </div>
            <div className="flex w-full max-w-275 flex-col">
              <h4 className="text-brand-byzantine">
                {data?.response?.total_dafw_cases}
              </h4>
              <p className="text-neutral-tones-text font-inter-variable text-sm leading-5 font-bold uppercase lg:text-base">
                TOTAL NUMBER OF CASES WITH DAYS AWAY FROM WORK
              </p>
            </div>
            <div className="flex w-full max-w-275 flex-col">
              <h4 className="text-brand-byzantine">
                {data?.response?.total_djtr_cases}
              </h4>
              <p className="text-neutral-tones-text font-inter-variable text-sm leading-5 font-bold uppercase lg:text-base">
                TOTAL NUMBER OF CASES WITH JOB TRANSFER OR RESTRICTION
              </p>
            </div>
            <div className="flex w-full max-w-275 flex-col">
              <h4 className="text-brand-byzantine">
                {data?.response?.total_other_cases}
              </h4>
              <p className="text-neutral-tones-text font-inter-variable text-sm leading-5 font-bold uppercase lg:text-base">
                TOTAL NUMBER OF OTHER RECORDABLE CASES
              </p>
            </div>
          </div>
        </div>
        <div className="border-neutral-tones-text/35 rounded-10 bg-card-backgrounds/5 flex flex-col overflow-hidden border sm:col-span-5 sm:gap-8">
          <div className="bg-brand-byzantine w-full p-15">
            <h4 className="text-neutral-tones-text-revert">Number of Days</h4>
          </div>
          <div className="flex flex-wrap justify-between gap-y-5 px-15 pt-15 pb-15 lg:gap-y-9 lg:pt-0">
            <div className="flex w-full min-w-275 flex-1 flex-col">
              <h4 className="text-brand-byzantine">
                {data?.response?.total_dafw_days}
              </h4>
              <p className="text-neutral-tones-text font-inter-variable text-sm leading-5 font-bold uppercase lg:text-base">
                TOTAL NUMBER OF DAYS AWAY FROM WORK
              </p>
            </div>
            <div className="flex w-full min-w-275 flex-1 flex-col">
              <h4 className="text-brand-byzantine">
                {data?.response?.total_djtr_days}
              </h4>
              <p className="text-neutral-tones-text font-inter-variable text-sm leading-5 font-bold uppercase lg:text-base">
                TOTAL NUMBER OF JOB TRANSFER OR RESTRICTION
              </p>
            </div>
          </div>
        </div>
        <div className="border-neutral-tones-text/35 rounded-10 bg-card-backgrounds/5 flex flex-col overflow-hidden border sm:col-span-7 sm:gap-8">
          <div className="bg-brand-byzantine w-full p-15">
            <h4 className="text-neutral-tones-text-revert">
              Injury and Illness Types
            </h4>
          </div>
          <div className="flex flex-wrap justify-between gap-y-9 px-15 pt-15 pb-15 lg:pt-0">
            <div className="flex w-full max-w-215 flex-col">
              <h4 className="text-brand-byzantine">
                {data?.response?.total_injuries}
              </h4>
              <p className="text-neutral-tones-text font-inter-variable text-sm leading-5 font-bold uppercase lg:text-base">
                INJURIES
              </p>
            </div>
            <div className="flex w-full max-w-215 flex-col">
              <h4 className="text-brand-byzantine">
                {data?.response?.total_skin_disorders}
              </h4>
              <p className="text-neutral-tones-text font-inter-variable text-sm leading-5 font-bold uppercase lg:text-base">
                SKIN DISORDERS
              </p>
            </div>
            <div className="flex w-full max-w-215 flex-col">
              <h4 className="text-brand-byzantine">
                {data?.response?.total_respiratory_conditions}
              </h4>
              <p className="text-neutral-tones-text font-inter-variable text-sm leading-5 font-bold uppercase lg:text-base">
                RESPIRATORY CONDITIONS
              </p>
            </div>
            <div className="flex w-full max-w-215 flex-col">
              <h4 className="text-brand-byzantine">
                {data?.response?.total_poisonings}
              </h4>
              <p className="text-neutral-tones-text font-inter-variable text-sm leading-5 font-bold uppercase lg:text-base">
                POISONINGS
              </p>
            </div>
            <div className="flex w-full max-w-215 flex-col">
              <h4 className="text-brand-byzantine">
                {data?.response?.total_hearing_loss}
              </h4>
              <p className="text-neutral-tones-text font-inter-variable text-sm leading-5 font-bold uppercase lg:text-base">
                HEARING LOSS
              </p>
            </div>
            <div className="flex w-full max-w-215 flex-col">
              <h4 className="text-brand-byzantine">
                {data?.response?.total_other_illnesses}
              </h4>
              <p className="text-neutral-tones-text font-inter-variable text-sm leading-5 font-bold uppercase lg:text-base">
                ALL OTHER ILLNESSES
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OshaSummaryModal;
